import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { scenarioNameFromSearch, buildMapScenario } from '../lib/map/mapScenarios';
import { Navigation2 } from 'lucide-react';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useFindProximity } from '../contexts/FindProximityContext';
import { findRemoteLog } from '../lib/findRemoteLog';
import { GLASS_SURFACES, CARD_SOLID_SURFACE_CLASS } from '../config/glassSurfaces';
import Button from './ui/Button';
import NothingYoursYet from './ui/NothingYoursYet';
import TrackingRadar from './find/TrackingRadar';
import { useFindRadar } from './find/useFindRadar';
import PermissionsStatus from './find/PermissionsStatus';
import FindPeerList from './find/FindPeerList';
import FindTopBar from './find/FindTopBar';
import { useFestivalMap } from '../hooks/queries/useFestivalMap';
import { parseFestivalMap, KIND_DISPLAY, venueBounds } from '../lib/map/festivalMapGeoJson';
import { CHIP_WORDS } from '../lib/map/kinds';
import { featureDisplayName } from '../lib/map/featureName';
import { CHIP_ORDER, amenitiesForChip, chipKindsFor, matchesChip } from '../lib/map/amenityFinder';

import { usePromptAuth } from './auth/AuthPrompt';
import useDeviceHeading from '../hooks/useDeviceHeading';
const GROUP_CARD_CLASS = `rounded-xl p-4 ${
  GLASS_SURFACES.cards ? 'event-card-surface' : CARD_SOLID_SURFACE_CLASS
}`;

export default function Find() {
  const promptAuth = usePromptAuth();
  const { session, authReady } = useCurrentUserProfile();

  const proximity = useFindProximity();
  const { sessionEventId, groupsError } = proximity;
  // No errors on the page (Sam, 2026-10-12): a weak-signal timeout printed "Request timed out after 9000ms" in red.
  // Problems go to the Find log once each; the page keeps showing what it has, and the no-service banner speaks for
  // the network.
  const issue =
    groupsError?.message ??
    proximity.startError ??
    (proximity.publishStatus?.lastErrorCode === '42501'
      ? 'publish 42501: not an active member'
      : null);
  useEffect(() => {
    if (issue)
      findRemoteLog(
        'warn',
        'find.page.issue',
        { message: String(issue).slice(0, 300) },
        { skipThrottle: true },
      );
  }, [issue]);

  // Dev only: ?scenario=<name> stands in a session of made-up friends (lib/map/mapScenarios.js) - the intro's Find shot
  // is ?scenario=tutorial. The radar reads bearings and distances only, so any box will do for the scenario's anchor.
  const location = useLocation();
  const scenarioName = scenarioNameFromSearch(location.search);
  const builtScenario = useMemo(
    () =>
      scenarioName
        ? buildMapScenario(scenarioName, [
            [-116.24, 33.68],
            [-116.23, 33.69],
          ])
        : null,
    [scenarioName],
  );
  // The welcome flow's radar turns with the phone's own compass (useDeviceHeading).
  const welcomeHeading = useDeviceHeading(builtScenario?.name === 'welcome');
  const scenario = useMemo(
    () =>
      builtScenario && welcomeHeading != null
        ? { ...builtScenario, headingDeg: welcomeHeading }
        : builtScenario,
    [builtScenario, welcomeHeading],
  );
  const {
    radarProps,
    permRows,
    permissionsOpen,
    setPermissionsOpen,
    handlePeerTap,
    onAskLocation,
    trackedPeerId: radarTracked,
    upcoming,
  } = useFindRadar({ scenario });

  // The event's map, for the chips and place search (Stage 6, 2026-10-26). Dev: ?event=<id> gives a scenario a venue.
  const devEventId = scenario ? new URLSearchParams(location.search).get('event') : null;
  const mapEventId = devEventId ?? sessionEventId ?? null;
  const { data: mapPayload } = useFestivalMap(mapEventId);
  // Before the window: the show's map if it has one, else its page (never Events - you already have a show).
  const { data: upcomingMap } = useFestivalMap(upcoming?.eventId ?? null);
  const model = useMemo(() => parseFestivalMap(mapPayload), [mapPayload]);
  const chipKinds = useMemo(() => chipKindsFor(model), [model]);
  // Where "nearest" is measured from: you. A scenario stands you in the middle of the venue.
  const scenarioSelf = useMemo(() => {
    if (scenario?.name === 'welcome') return scenario.self; // the welcome flow stands you somewhere real
    if (!scenario || !model) return null;
    const [[w, s0], [e, n]] = venueBounds(model);
    return { lat: (s0 + n) / 2, lng: (w + e) / 2 };
  }, [scenario, model]);
  const [activeKinds, setActiveKinds] = useState(() => new Set());
  // An amenity you tapped is tracked like a friend - the arrow, the readout - until you tap it again or a friend.
  const [selectedAmenityId, setSelectedAmenityId] = useState(null);
  const [query, setQuery] = useState('');
  const [emphasizedPeerId, setEmphasizedPeerId] = useState(null);
  // You move: while a chip is on, the nearest few are re-read every few seconds.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!activeKinds.size) return undefined;
    const id = window.setInterval(() => setTick((t) => t + 1), 3000);
    return () => window.clearInterval(id);
  }, [activeKinds.size]);
  useEffect(() => {
    setActiveKinds(new Set());
    setSelectedAmenityId(null);
    setQuery('');
  }, [mapEventId]);
  const selfNow = useCallback(
    () => scenarioSelf ?? proximity.getSelfPosition?.() ?? null,
    [scenarioSelf, proximity],
  );
  const radarAmenities = useMemo(() => {
    const self = selfNow();
    const seen = new Set();
    const out = [];
    for (const kind of activeKinds) {
      for (const am of amenitiesForChip(model, kind, self)) {
        if (seen.has(am.feature.id)) continue;
        seen.add(am.feature.id);
        out.push({
          ...am,
          name: featureDisplayName(am.feature, KIND_DISPLAY[am.feature.kind]?.label),
        });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tick re-reads your position
  }, [model, activeKinds, selfNow, tick]);
  // A chip on: its amenities join the radar, nothing picked (Sam, 2026-10-26: no pre-selection). Off: they leave, and the
  // pick with them if it was one. A place picked by name from search is the one exception: you asked for that one.
  const toggleChip = useCallback(
    (kind, pick = null) => {
      setActiveKinds((prev) => {
        const next = new Set(prev);
        if (next.has(kind) && !pick) {
          next.delete(kind);
          setSelectedAmenityId((sel) => {
            const f = sel && model?.features.find((x) => x.id === sel);
            return f && matchesChip(kind, f) ? null : sel;
          });
        } else {
          next.add(kind);
          if (pick) setSelectedAmenityId(pick.id);
        }
        return next;
      });
    },
    [model],
  );
  // The radar's rows: your friends, then the amenities the chips put up (ids prefixed so they never meet a person's).
  const AMENITY_ID = 'amenity:';
  const amenityRows = useMemo(
    () =>
      radarAmenities.map((am) => ({
        peerId: AMENITY_ID + am.feature.id,
        displayName: am.name,
        photoUrl: null,
        amenityKind: am.feature.kind,
        bearingDeg: am.bearingDeg,
        display: {
          kind: 'gps-live',
          distanceMeters: am.distanceMeters,
          gpsLastKnownAt: null,
          horizontalAccuracyMeters: 3,
          bearingUncertaintyDeg: 2,
          bearingSource: 'gps',
        },
      })),
    [radarAmenities],
  );
  // Tapping an amenity tracks it (again: lets it go); tapping a friend tracks them and lets the amenity go.
  const onPeerTap = useCallback(
    (peerId) => {
      if (peerId.startsWith(AMENITY_ID)) {
        const id = peerId.slice(AMENITY_ID.length);
        setSelectedAmenityId((sel) => (sel === id ? null : id));
        return;
      }
      setSelectedAmenityId(null);
      handlePeerTap(peerId);
    },
    [handlePeerTap],
  );

  // Search: places (the chips' kinds by word, and named amenities) and people, one list.
  const places = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !model) return [];
    const out = [];
    for (const kind of chipKinds) {
      const word = (CHIP_WORDS[kind] ?? '').toLowerCase();
      const label = (KIND_DISPLAY[kind]?.label ?? '').toLowerCase();
      if (word.includes(q) || label.includes(q))
        out.push({
          key: `kind:${kind}`,
          kind,
          chip: kind,
          name: KIND_DISPLAY[kind]?.label ?? word,
        });
    }
    const named = new Set();
    for (const f of model.features) {
      const name = featureDisplayName(f);
      if (!f.name || !name.toLowerCase().includes(q) || named.has(name.toLowerCase())) continue;
      const chip = CHIP_ORDER.find((c) => matchesChip(c, f));
      if (!chip || out.some((p) => p.name.toLowerCase() === name.toLowerCase())) continue;
      named.add(name.toLowerCase());
      out.push({ key: `f:${f.id}`, kind: f.kind, chip, name, feature: f });
    }
    return out.slice(0, 6);
  }, [query, model, chipKinds]);
  const radarPeers = scenario ? scenario.peers : proximity.peers;
  const people = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return (radarPeers ?? [])
      .filter((p) => (p.displayName || '').toLowerCase().includes(q))
      .slice(0, 4);
  }, [query, radarPeers]);
  const pickPlace = useCallback(
    (p) => {
      toggleChip(p.chip, p.feature ?? null);
      setQuery('');
    },
    [toggleChip],
  );
  const pickPerson = useCallback(
    (peerId) => {
      if (radarTracked !== peerId) handlePeerTap(peerId);
      setSelectedAmenityId(null);
      setEmphasizedPeerId(peerId);
      setQuery('');
    },
    [radarTracked, handlePeerTap],
  );
  const topBar = (status) => (
    <FindTopBar
      query={query}
      onQuery={setQuery}
      places={places}
      people={people}
      onPickPlace={pickPlace}
      onPickPerson={pickPerson}
      chipKinds={chipKinds}
      activeKinds={activeKinds}
      onToggleChip={(k) => toggleChip(k)}
      status={status}
    />
  );
  const selectedOnRadar =
    selectedAmenityId && radarAmenities.some((am) => am.feature.id === selectedAmenityId);
  const radar = (
    <TrackingRadar
      {...radarProps}
      peers={[...(radarProps.peers ?? []), ...amenityRows]}
      trackedPeerId={selectedOnRadar ? AMENITY_ID + selectedAmenityId : radarProps.trackedPeerId}
      onPeerTap={onPeerTap}
    />
  );

  // A scenario (the welcome flow, dev ?scenario=) is the same page on made-up people: it skips the sign-in gates and the
  // status button, and every other part of the layout is shared, so a change to Find shows in the tour too.
  if (!scenario && !authReady) {
    return (
      <div className="min-h-[50vh] px-[18px] py-12">
        <div className="mb-6 h-8 w-40 rounded skeleton-shimmer" aria-hidden />
        <div className={`rounded-xl p-6 ${GROUP_CARD_CLASS}`}>
          <div className="mb-3 h-4 w-3/4 rounded skeleton-shimmer" aria-hidden />
          <div className="h-4 w-1/2 rounded skeleton-shimmer" aria-hidden />
        </div>
      </div>
    );
  }

  if (!scenario && !session) {
    return (
      <div className="min-h-[50vh] px-[18px] py-12">
        <div className="py-24 text-center">
          <Navigation2 className="mx-auto mb-4 h-12 w-12 text-dark-muted" />
          <h1 className="mb-2 text-2xl font-bold text-dark-text">Find your group nearby</h1>
          <p className="mx-auto mb-6 max-w-md text-dark-muted">
            Log in to see group members around you.
          </p>
          <Button size="lg" onClick={() => promptAuth()}>
            Log in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-var(--app-content-top)-var(--app-content-bottom))] flex-col px-[18px] pb-2 pt-4">
      <h1 className="sr-only">Find</h1>
      {topBar(
        !scenario && proximity.isNative ? (
          <PermissionsStatus
            rows={permRows}
            onAskLocation={onAskLocation}
            className="find-perm-status ml-auto"
            open={permissionsOpen}
            onOpenChange={setPermissionsOpen}
          />
        ) : null,
      )}
      <div className="-mx-[18px] mb-0 flex flex-1 items-center justify-center">
        <div className="w-full">{radar}</div>
      </div>

      {/* Nothing on: one line and the way to fix it (Sam, 2026-10-12 - the same on Maps, Sets and Groups). Something on but
          nobody on the radar: the radar says "Nobody here" and the page explains nothing else - one rule, whether it is
          no follow-backs, no group, or a group not sharing (Sam, 2026-10-21: no prompts, no paragraphs). */}
      {upcoming ? (
        <NothingYoursYet
          className="!py-6"
          text={`Find turns on ${upcoming.turnsOn}.`}
          action={
            upcomingMap
              ? { label: 'Open map', to: `/map/${encodeURIComponent(upcoming.eventId)}` }
              : { label: 'See event', to: `/events/${encodeURIComponent(upcoming.eventId)}` }
          }
        />
      ) : (
        !scenario &&
        !proximity.sessionLoading &&
        !sessionEventId && (
          <NothingYoursYet className="!py-6" text="Find turns on at your next show." />
        )
      )}
      <FindPeerList
        className="mt-6"
        peers={scenario ? scenario.peers : proximity.peers}
        trackedPeerId={scenario ? radarTracked : proximity.trackedPeerId}
        onPeerTap={onPeerTap}
        emphasizedPeerId={emphasizedPeerId}
      />
    </div>
  );
}
